import React, { useCallback, useMemo } from 'react';

import Autocomplete from '~/components/core/Molecules/Fields/AutoComplete';

import type { GbgSuburbSuggestion } from './types';
import { useGbgSuburbAutocomplete } from './useGbgSuburbAutocomplete';

interface GbgSuburbAutocompleteProps {
  id: string;
  label: string;
  country: string;
  onSuburbSelected: (suggestion: GbgSuburbSuggestion) => void;
  className?: string;
  disabled?: boolean;
  value?: string;
  error?: string;
  helperText?: string;
  onInputChange?: (event: React.SyntheticEvent | null, value: string) => void;
  onBlur?: () => void;
}

const GbgSuburbAutocomplete = ({
  id,
  label,
  country,
  onSuburbSelected,
  className = '',
  disabled,
  value,
  error,
  helperText,
  onInputChange,
  onBlur,
}: GbgSuburbAutocompleteProps) => {
  const { suggestions, isLoading, search, clearSuggestions } = useGbgSuburbAutocomplete({
    country,
  });

  const suggestionsDict = useMemo(() => {
    const dict: Record<string, GbgSuburbSuggestion> = {};
    for (const suggestion of suggestions) {
      dict[suggestion.full_address] = suggestion;
    }
    return dict;
  }, [suggestions]);

  const handleInputChanged = useCallback(
    (event: React.SyntheticEvent | null, inputValue: string, reason: string) => {
      if (reason === 'input') {
        if (inputValue) {
          search(inputValue);
        } else {
          clearSuggestions();
        }
      }

      if (onInputChange) {
        onInputChange(event, inputValue);
      }
    },
    [search, clearSuggestions, onInputChange]
  );

  const handleSuggestionSelected = useCallback(
    (_: React.SyntheticEvent | undefined, optionKey?: string | null) => {
      if (!optionKey) return;

      const suggestion = suggestionsDict[optionKey];
      if (suggestion) {
        clearSuggestions();
        onSuburbSelected(suggestion);
      }
    },
    [suggestionsDict, clearSuggestions, onSuburbSelected]
  );

  return (
    <Autocomplete
      className={className}
      id={id}
      freeSolo
      options={Object.keys(suggestionsDict)}
      getOptionLabel={(optionKey: string) => suggestionsDict[optionKey]?.full_address || optionKey}
      onChange={handleSuggestionSelected}
      value={value || null}
      disabled={disabled}
      loading={isLoading}
      onInputChange={handleInputChanged}
      label={label}
      textFieldProps={{
        error: !!error,
        helperText: error || helperText,
        onBlur,
      }}
    />
  );
};

export default GbgSuburbAutocomplete;
